/**
 * Minimal WebVTT parsing for rendering our own caption overlay.
 *
 * Native `::cue` styling only supports a handful of properties and browsers
 * disagree on the rest, so the cues are rendered as DOM instead. That also
 * removes the need for a blob URL on the <track>.
 */

const decodeEntities = (value) =>
  value
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'");

const toSeconds = (stamp) => {
  const match = String(stamp).match(/^(\d+):(\d{2}):(\d{2})[.,](\d{1,3})$/);
  if (!match) return null;
  return (
    Number(match[1]) * 3600 +
    Number(match[2]) * 60 +
    Number(match[3]) +
    Number(match[4].padEnd(3, "0")) / 1000
  );
};

const cleanCueText = (value) =>
  decodeEntities(
    value
      // Voice/class/italic markup is presentational; keep the words only.
      .replace(/<[^>]+>/g, " ")
      .replace(/\s+/g, " "),
  ).trim();

/** Returns `[{ start, end, text }]`, ignoring STYLE/NOTE/REGION preamble blocks. */
export const parseVttCues = (vtt) => {
  if (typeof vtt !== "string" || !vtt.trim()) return [];

  const cues = [];
  for (const block of vtt.split(/\r?\n\r?\n/)) {
    const timing = block.match(
      /(\d+:\d{2}:\d{2}[.,]\d{1,3})\s*-->\s*(\d+:\d{2}:\d{2}[.,]\d{1,3})/,
    );
    if (!timing) continue;

    const start = toSeconds(timing[1]);
    const end = toSeconds(timing[2]);
    if (start === null || end === null || end <= start) continue;

    const lines = block
      .split(/\r?\n/)
      .filter((line) => !line.includes("-->") && !/^(WEBVTT|STYLE|NOTE|REGION)/.test(line));
    const text = cleanCueText(lines.join(" "));
    if (text) cues.push({ start, end, text });
  }

  return cues.sort((a, b) => a.start - b.start);
};

/** Cues visible at `time`. Usually one, but overlaps are legal in WebVTT. */
export const getActiveCues = (cues, time) => {
  if (!Array.isArray(cues) || !Number.isFinite(time)) return [];
  return cues.filter((cue) => time >= cue.start && time <= cue.end);
};
